import { createFileRoute } from "@tanstack/react-router";
import CheckIcon from "lucide-react/dist/esm/icons/check";
import CopyIcon from "lucide-react/dist/esm/icons/copy";
import { useId, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { PageBody, PageHeader, Panel } from "@/components/shell/page-frame";
import { DefinitionRow, TabNav } from "@/components/shell/tab-nav";
import { StatusBadge, StatusDot } from "@/components/status/status-badge";
import type { Tone } from "@/components/status/tone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const settingsSearchSchema = z.object({
  tab: z.enum(["connections", "automation"]).catch("connections").default("connections")
});

export const Route = createFileRoute("/settings")({
  validateSearch: settingsSearchSchema,
  component: SettingsPage
});

/**
 * 設定。第5章のシステム構成（Shopify・Worker・D1・BYOセンダー）と、
 * 第6章の2層AI設計（開発=Shopify公式 / 運用=Harness独自MCP）を管理する。
 */
function SettingsPage() {
  const { api, apiBaseUrl } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <>
      <PageHeader kind="data" title="設定" description="接続・センダー・自動化の構成" />

      <PageBody density="dense" className="mx-auto w-full max-w-220">
        <TabNav
          className="mb-4"
          value={search.tab}
          onChange={(tab) => navigate({ search: { tab } })}
          items={[
            { value: "connections", label: "接続・センダー" },
            { value: "automation", label: "自動化・MCP" }
          ]}
        />

        {search.tab === "connections" ? (
          <ConnectionsTab apiBaseUrl={apiBaseUrl} hasApi={Boolean(api)} />
        ) : (
          <AutomationTab />
        )}
      </PageBody>
    </>
  );
}

interface ServiceLine {
  name: string;
  detail: string;
  tone: Tone;
  status: string;
}

function ConnectionsTab({ apiBaseUrl, hasApi }: { apiBaseUrl: string; hasApi: boolean }) {
  const [senderConnected] = useState(true);

  const services: ServiceLine[] = [
    {
      name: "Shopify ストア",
      detail: "aurora-living.myshopify.com · Custom App（審査不要）",
      tone: "success",
      status: "接続済"
    },
    {
      name: "Shopify ストア",
      detail: "kaede-craft.myshopify.com · Custom App",
      tone: "success",
      status: "接続済"
    },
    {
      name: "Cloudflare Worker",
      detail: `${apiBaseUrl} · Webhook受信 / cron配信`,
      tone: hasApi ? "success" : "critical",
      status: hasApi ? "稼働中" : "未接続"
    },
    { name: "D1 (SQLite)", detail: "顧客・RFMスコア・配信履歴", tone: "success", status: "稼働中" }
  ];

  return (
    <div className="grid gap-4">
      <Panel title="接続" description="Webhook / REST API 方式。保有コスト0円で常時稼働" flush>
        <ul className="divide-y divide-line">
          {services.map((service) => (
            <li
              key={`${service.name}-${service.detail}`}
              className="flex items-center gap-3 px-4 py-3"
            >
              <StatusDot tone={service.tone} pulse={service.tone === "critical"} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-fg">{service.name}</span>
                <span className="block truncate text-xs text-fg-subtle">{service.detail}</span>
              </span>
              <StatusBadge tone={service.tone} glyph={false}>
                {service.status}
              </StatusBadge>
            </li>
          ))}
        </ul>
      </Panel>

      {/* BYOセンダー: 自分のキーで送信。コンタクト数課金の廃止（第7章） */}
      <Panel
        title="BYOセンダー"
        description="自分の SES / Resend キーで送信。送信実費のみ・保有課金なし"
      >
        <dl className="grid gap-0">
          <DefinitionRow label="プロバイダ">
            <span className="text-fg">Amazon SES</span>
          </DefinitionRow>
          <DefinitionRow label="送信ドメイン">
            <span className="num text-fg">mail.aurora-living.jp</span>
          </DefinitionRow>
          <DefinitionRow label="認証">
            <span className="inline-flex items-center gap-1.5">
              <StatusBadge tone="success">SPF/DKIM 検証済</StatusBadge>
            </span>
          </DefinitionRow>
          <DefinitionRow label="APIキー">
            <span className="num text-fg-muted">AKIA••••••••••••7F2C</span>
          </DefinitionRow>
        </dl>
        <div className="mt-3 flex items-center gap-2">
          <Button type="button" size="sm" variant="outline">
            キーを更新
          </Button>
          <span
            className={cn(
              "inline-flex items-center gap-1.5 text-xs",
              senderConnected ? "text-success-fg" : "text-critical-fg"
            )}
          >
            <StatusDot tone={senderConnected ? "success" : "critical"} />
            送信テスト成功
          </span>
        </div>
      </Panel>
    </div>
  );
}

function AutomationTab() {
  const mcpEndpoint = "https://worker.shopify-harness.example/mcp";
  const autoApplyId = useId();
  const [autoApplyLowRisk, setAutoApplyLowRisk] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(mcpEndpoint);
      setCopied(true);
      toast.success("MCPエンドポイントをコピーしました");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("コピーできませんでした");
    }
  };

  return (
    <div className="grid gap-4">
      {/* 2層AI設計（第6章）: 開発=Shopify公式 / 運用=Harness独自MCP */}
      <Panel title="AI連携（2層設計）" description="開発は公式基盤、運用は同梱MCP">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-line bg-surface p-3">
            <p className="eyebrow">開発レイヤー</p>
            <p className="mt-1 text-sm font-medium text-fg">Shopify 公式 AI Toolkit</p>
            <p className="mt-1 text-xs text-fg-subtle">
              Dev MCP・スキル・CLI。スキーマ検証で推測実装を排除。
            </p>
            <StatusBadge tone="success" className="mt-2">
              有効
            </StatusBadge>
          </div>
          <div className="rounded-lg border border-line bg-surface p-3">
            <p className="eyebrow">運用レイヤー</p>
            <p className="mt-1 text-sm font-medium text-fg">Harness 同梱 MCP</p>
            <p className="mt-1 text-xs text-fg-subtle">
              フロー作成・セグメント照会・配信状況を自然言語で操作。
            </p>
            <StatusBadge tone="success" className="mt-2">
              有効
            </StatusBadge>
          </div>
        </div>

        <div className="mt-3">
          <p className="eyebrow mb-1">MCPエンドポイント</p>
          <div className="flex items-center gap-2">
            <Input readOnly value={mcpEndpoint} className="num h-8 font-mono text-xs" />
            <Button type="button" size="sm" variant="outline" onClick={copy}>
              {copied ? <CheckIcon aria-hidden="true" /> : <CopyIcon aria-hidden="true" />}
              コピー
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-fg-subtle">
            Claude Code / Codex / Cursor
            から接続し、「RFM上位20%に休眠フローを作って」のように操作できます。
          </p>
        </div>
      </Panel>

      <Panel title="cron スケジュール" description="定期実行するジョブ" flush>
        <ul className="divide-y divide-line">
          {[
            { name: "RFMスコア再計算", schedule: "毎日 03:00", tone: "success" as const },
            { name: "休眠・滞留の定期スキャン", schedule: "毎日 04:00", tone: "success" as const },
            { name: "KPI異常検知", schedule: "5分ごと", tone: "success" as const },
            { name: "引用モニタリング", schedule: "毎週月曜 06:00", tone: "success" as const },
            { name: "週次レポート生成", schedule: "毎週月曜 07:00", tone: "success" as const }
          ].map((job) => (
            <li key={job.name} className="flex items-center gap-3 px-4 py-2.5">
              <StatusDot tone={job.tone} />
              <span className="min-w-0 flex-1 truncate text-sm text-fg">{job.name}</span>
              <span className="num text-xs text-fg-muted">{job.schedule}</span>
            </li>
          ))}
        </ul>
      </Panel>

      {/* 配信の自動改善(4-1)の承認ポリシー */}
      <Panel title="承認ポリシー" description="AIの変更をどこまで自動適用するか">
        <label htmlFor={autoApplyId} className="flex items-start justify-between gap-4">
          <span>
            <span className="block text-sm font-medium text-fg">低リスクな変更を自動適用する</span>
            <span className="mt-0.5 block text-xs text-fg-subtle">
              件名の微修正など低リスクな改善案を承認なしで適用します。
              シナリオの分岐変更や配信停止は常に人の承認が必要です。
            </span>
          </span>
          <Switch
            id={autoApplyId}
            checked={autoApplyLowRisk}
            onCheckedChange={setAutoApplyLowRisk}
          />
        </label>
        {autoApplyLowRisk ? (
          <p className="mt-3 rounded-md border border-warning-line bg-warning-subtle px-2.5 py-1.5 text-xs text-warning-fg">
            低リスク変更の自動適用が有効です。適用結果はシナリオの改善履歴で確認できます。
          </p>
        ) : null}
      </Panel>
    </div>
  );
}
